import { useLogger } from '@omnia/ui/src/lib/hooks/useLogger'
import type { Member as OccMember } from '@vista-digital/ocapi-types/v1'
import { getMember, isMemberSignedIn, loadMember, signMemberOut } from '@vista-digital/occ-domain'
import { reaction } from 'mobx'
import { useEffect, useState } from 'react'

export const useGetMember = () => {
    const isSignedIn = isMemberSignedIn()
    const [member, setMember] = useState<Member | undefined>()
    const logger = useLogger('useMember')
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        /* to make `mobx` store to behave like a normal react state*/
        const dispose = reaction(
            () => getMember(),
            () => {
                void (async () => {
                    try {
                        const memberSignedInAndNoDataLoaded = !getMember() && isMemberSignedIn()
                        logger.info('memberSignedInAndNoDataLoaded', memberSignedInAndNoDataLoaded)
                        const memberData = memberSignedInAndNoDataLoaded ? await loadMember(true) : getMember()
                        if (!memberData) {
                            logger.warn('Member data is not available')
                            return
                        }
                        const { familyName = '', givenName = '' } = memberData?.personalDetails.name || {}
                        const initials = `${givenName?.[0] ?? ''}${familyName?.[0] ?? ''}`.toUpperCase()
                        const fullName = `${givenName} ${familyName}`.trim()
                        setMember({ ...memberData, initials, fullName })
                    } catch (e) {
                        logger.error('Error loading member data', e)
                        await signMemberOut()
                        logger.info('Signed out member due to error loading data')
                    } finally {
                        setLoading(false)
                    }
                })()
            },
            { fireImmediately: true }
        )
        return () => dispose()
    }, [isSignedIn, logger])

    return { isSignedIn, member, loading }
}

// auto extends OCCMember interface
export interface Member extends OccMember {
    initials: string
    fullName: string
}
